import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type PublishedMentorStory,
  type MentorStoryImage,
  deleteMentorStoryImage,
  getMentorStories,
  getMentorStoryPresignedUrl,
  saveMentorStory,
} from "@/lib/api/mentor-stories";

export const mentorStoriesKey = (mentorId: string) => ["mentor-stories", mentorId];

export const useMentorStories = (mentorId: string, enabled = true) => {
  return useQuery({
    queryKey: mentorStoriesKey(mentorId),
    queryFn: () => getMentorStories(mentorId),
    enabled,
  });
};

/** @deprecated Direct write of published slides. Story review approval publishes them now. */
export const useSaveMentorStory = (mentorId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: { storyType: string; images: MentorStoryImage[] }) =>
      saveMentorStory(mentorId, body),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: mentorStoriesKey(mentorId),
      });

      const previousStories = queryClient.getQueryData<PublishedMentorStory | null>(
        mentorStoriesKey(mentorId),
      );

      return { previousStories };
    },
    onError: (_err, _, context) => {
      queryClient.setQueryData(
        mentorStoriesKey(mentorId),
        context?.previousStories,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: mentorStoriesKey(mentorId),
      });
    },
  });
};

/** @deprecated Direct delete of a published slide. Story review takedown clears them now. */
export const useDeleteMentorStoryImage = (mentorId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ imageKey }: { imageKey: string }) =>
      deleteMentorStoryImage(mentorId, imageKey),
    onMutate: async ({ imageKey }) => {
      await queryClient.cancelQueries({
        queryKey: mentorStoriesKey(mentorId),
      });

      const previousStories = queryClient.getQueryData<PublishedMentorStory | null>(
        mentorStoriesKey(mentorId),
      );

      queryClient.setQueryData<PublishedMentorStory | null>(
        mentorStoriesKey(mentorId),
        (old) =>
          old
            ? {
                ...old,
                images: old.images.filter((img) => img.key !== imageKey),
              }
            : old,
      );

      return { previousStories };
    },
    onError: (_err, _, context) => {
      queryClient.setQueryData(
        mentorStoriesKey(mentorId),
        context?.previousStories,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: mentorStoriesKey(mentorId),
      });
    },
  });
};

export const usePresignedStoryUrl = (mentorId: string) => {
  return useMutation({
    mutationKey: ["mentor-story-presigned-url"],
    mutationFn: ({ filename, contentType }: { filename: string; contentType: string }) =>
      getMentorStoryPresignedUrl(filename, contentType, mentorId),
  });
};
